import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import { http } from '../../lib/api';
import { buildDisplayNameFromUser } from '../../lib/displayName';
import { X, Save, Building2, Wrench, FileText, StickyNote, Image as ImageIcon } from 'lucide-react';
import ImagenesPresupuesto from './ImagenesPresupuesto';

const PresupuestoForm = ({ presupuesto = null, onClose, onSave, readOnly = false }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [equipos, setEquipos] = useState([]);
  const [equiposCliente, setEquiposCliente] = useState([]); // Todos los equipos del cliente
  const [equiposFiltrados, setEquiposFiltrados] = useState([]); // Equipos filtrados por sucursal
  const [sucursales, setSucursales] = useState([]);

  const [formData, setFormData] = useState({
    cliente_id: presupuesto?.cliente_id || '',
    cliente_nombre: presupuesto?.cliente_nombre || '',
    sucursal_id: presupuesto?.sucursal_id || '',
    sucursal_nombre: presupuesto?.sucursal_nombre || '',
    equipo_id: presupuesto?.equipo_id || '',
    equipo_nombre: presupuesto?.equipo_nombre || '',
    descripcion: presupuesto?.descripcion || '',
    detalle: presupuesto?.detalle || '',
    estado: presupuesto?.estado || 'borrador',
    notas_internas: presupuesto?.notas_internas || '',
    imagenes: presupuesto?.imagenes || []
  });

  // Función para filtrar equipos por sucursal
  const filtrarEquiposPorSucursal = (sucursalNombre) => {
    if (!sucursalNombre) {
      setEquiposFiltrados(equiposCliente);
      return;
    }
    const filtrados = equiposCliente.filter(equipo => equipo.sucursal === sucursalNombre);
    setEquiposFiltrados(filtrados);
  };

  // Cargar clientes y equipos iniciales
  useEffect(() => {
    const cargarClientes = async () => {
      try {
        const response = await http.get('/clientes');
        setClientes(response.data || []);
      } catch (error) {
        console.error('Error al cargar clientes:', error);
        toast({
          title: "Error al cargar clientes",
          description: error.response?.data?.detail || error.message,
          variant: "destructive",
        });
      }
    };
    cargarClientes();
  }, [toast]);

  // Cargar sucursales y equipos cuando cambia el cliente
  useEffect(() => {
    const cargarSucursalesYEquipos = async () => {
      if (!formData.cliente_id) {
        setSucursales([]);
        setEquiposCliente([]);
        setEquiposFiltrados([]);
        return;
      }

      // Validar que el cliente_id sea un ID válido
      const esObjectId = formData.cliente_id.length === 24 && /^[0-9a-fA-F]{24}$/.test(formData.cliente_id);
      const esUUID = formData.cliente_id.includes('-') && formData.cliente_id.length >= 32;
      
      if (!esObjectId && !esUUID) {
        setSucursales([]);
        setEquiposCliente([]);
        setEquiposFiltrados([]);
        return;
      }

      try {
        // Encontrar el cliente seleccionado
        const clienteSeleccionado = clientes.find(c => c.id === formData.cliente_id || c._id === formData.cliente_id);
        
        // 1) Obtener sucursales desde el backend
        let sucursalesCargadas = [];
        try {
          const token = localStorage.getItem("token");
          const sucRes = await http.get(`/clientes/${formData.cliente_id}/sucursales`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const rawSucursales = Array.isArray(sucRes.data) ? sucRes.data : [];
          sucursalesCargadas = rawSucursales.map(s => {
            if (typeof s === 'string') return s;
            if (s && typeof s === 'object' && s.nombre) return s.nombre;
            return String(s);
          }).filter(Boolean);
        } catch (sErr) {
          // fallback a sucursales del objeto cliente
        }

        // 2) Combinar con sucursales del cliente
        if (clienteSeleccionado && Array.isArray(clienteSeleccionado.sucursales)) {
          const sucursalesNormalizadas = (clienteSeleccionado.sucursales || [])
            .map(s => {
              if (typeof s === 'string') return s;
              if (s && typeof s === 'object') return s.nombre || String(s);
              return null;
            })
            .filter(Boolean);
          const uniqueSucursales = new Set();
          (sucursalesCargadas || []).forEach(s => uniqueSucursales.add(String(s)));
          sucursalesNormalizadas.forEach(s => uniqueSucursales.add(String(s)));
          sucursalesCargadas = Array.from(uniqueSucursales).filter(s => s !== '[object Object]');
        }

        // 3) Cargar equipos del cliente
        const token = localStorage.getItem("token");
        const equiposResponse = await http.get('/equipos/', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const todosEquipos = equiposResponse.data || [];
        const equiposDelCliente = todosEquipos.filter(equipo => {
          const coincidePorId = equipo.cliente_id === formData.cliente_id || 
                               equipo.cliente?._id === formData.cliente_id ||
                               equipo.cliente?.id === formData.cliente_id;
          const clienteSel = clientes.find(c => c.id === formData.cliente_id || c._id === formData.cliente_id);
          const coincidePorNombre = clienteSel && 
                                   (equipo.cliente?.nombre === clienteSel.nombre ||
                                    (equipo.cliente_nombre && equipo.cliente_nombre === clienteSel.nombre));
          return coincidePorId || coincidePorNombre;
        });
        
        setEquiposCliente(equiposDelCliente);

        // 4) Si no hay sucursales, derivarlas de los equipos
        if (!sucursalesCargadas || sucursalesCargadas.length === 0) {
          const derivadas = [...new Set(equiposDelCliente.map(eq => eq.sucursal).filter(Boolean))];
          sucursalesCargadas = derivadas.length > 0 ? derivadas : ['Principal'];
        }
        setSucursales(sucursalesCargadas);
        
        if (formData.sucursal_nombre) {
          filtrarEquiposPorSucursal(formData.sucursal_nombre);
        } else {
          setEquiposFiltrados(equiposDelCliente);
        }
        
      } catch (error) {
        console.error('Error al cargar datos del cliente:', error);
        toast({
          title: "Error al cargar datos del cliente",
          description: error.response?.data?.detail || error.message,
          variant: "destructive",
        });
      }
    };

    if (clientes.length > 0) {
      cargarSucursalesYEquipos();
    }
  }, [formData.cliente_id, clientes, toast]);

  // Filtrar equipos cuando cambia la sucursal
  useEffect(() => {
    if (equiposCliente.length > 0 && formData.sucursal_nombre) {
      filtrarEquiposPorSucursal(formData.sucursal_nombre);
    }
  }, [formData.sucursal_nombre, equiposCliente]);

  const handleClienteChange = (e) => {
    const clienteId = e.target.value;
    const cliente = clientes.find(c => (c.id || c._id) === clienteId);
    
    setFormData(prev => ({
      ...prev,
      cliente_id: clienteId,
      cliente_nombre: cliente ? cliente.nombre : '',
      sucursal_id: '',
      sucursal_nombre: ''
    }));
    
    setSucursales([]);
    setEquiposCliente([]);
    setEquipos([]);
  };

  const handleSucursalChange = (e) => {
    const sucursalNombre = e.target.value;
    
    setFormData(prev => ({
      ...prev,
      sucursal_id: sucursalNombre,
      sucursal_nombre: sucursalNombre
    }));

    filtrarEquiposPorSucursal(sucursalNombre || null);
  };

  const handleEquipoChange = (e) => {
    const equipoId = e.target.value;
    const equipo = equiposFiltrados.find(eq => eq._id === equipoId);
    
    setFormData(prev => ({
      ...prev,
      equipo_id: equipoId,
      equipo_nombre: equipo ? `${equipo.marca} ${equipo.modelo} (${equipo.numero_serie})` : ''
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImagenesChange = (nuevasImagenes) => {
    setFormData(prev => ({
      ...prev,
      imagenes: nuevasImagenes
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.cliente_id) {
      toast({
        title: "Error de validación",
        description: "Debe seleccionar un cliente",
        variant: "destructive",
      });
      return;
    }

    if (!formData.descripcion.trim()) {
      toast({
        title: "Error de validación", 
        description: "La descripción es obligatoria",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      
      const dataToSend = {
        ...formData,
        tecnico_id: user._id,
        tecnico_nombre: buildDisplayNameFromUser(user)
      };

      let response;
      if (presupuesto) {
        // Editar existente
        response = await http.put(`/presupuestos/${presupuesto._id}`, dataToSend);
      } else {
        // Crear nuevo
        response = await http.post('/presupuestos', dataToSend);
      }

      toast({
        title: "Éxito",
        description: presupuesto ? 'Presupuesto actualizado correctamente' : 'Presupuesto creado correctamente',
      });
      
      if (onSave) onSave(response.data);
      if (onClose) onClose();
      
    } catch (error) {
      console.error('Error al guardar presupuesto:', error);
      toast({
        title: "Error al guardar",
        description: error.response?.data?.detail || error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const puedeEditar = () => {
    if (readOnly) return false;
    if (user.rol === 'admin') return true;
    if (user.rol === 'tecnico' && formData.estado === 'borrador') return true;
    return false;
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-emerald-600 to-teal-600 rounded-t-2xl">
          <div className="text-white">
            <h2 className="text-lg font-bold">
              {readOnly ? 'Ver Presupuesto' : presupuesto ? 'Editar Presupuesto' : 'Nuevo Presupuesto'}
            </h2>
            <p className="text-emerald-200 text-xs mt-0.5">
              {readOnly ? 'Modo solo lectura' : 'Completá los datos del trabajo a presupuestar'}
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scroll body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {/* ── Sección: Cliente ── */}
          <div className="space-y-3">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700 uppercase tracking-wide">
              <Building2 className="w-4 h-4 text-emerald-500" />
              Cliente y Ubicación
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Cliente <span className="text-red-500">*</span></label>
                <select
                  value={formData.cliente_id}
                  onChange={handleClienteChange}
                  disabled={!puedeEditar()}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                  required
                >
                  <option value="">Seleccionar cliente...</option>
                  {clientes.map(cliente => (
                    <option key={cliente.id || cliente._id} value={cliente.id || cliente._id}>
                      {cliente.nombre}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Sucursal</label>
                <select
                  value={formData.sucursal_nombre}
                  onChange={handleSucursalChange}
                  disabled={!puedeEditar() || !formData.cliente_id}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                >
                  <option value="">Sin sucursal específica</option>
                  {sucursales.map(sucursal => (
                    <option key={sucursal} value={sucursal}>{sucursal}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* ── Sección: Equipo ── */}
          <div className="space-y-3">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700 uppercase tracking-wide">
              <Wrench className="w-4 h-4 text-emerald-500" />
              Equipo
            </h3>
            <select
              value={formData.equipo_id}
              onChange={handleEquipoChange}
              disabled={!puedeEditar()}
              className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
            >
              <option value="">Sin equipo específico</option>
              {equiposFiltrados.map(equipo => (
                <option key={equipo._id} value={equipo._id}>
                  {equipo.marca} {equipo.modelo} — S/N: {equipo.numero_serie}
                </option>
              ))}
            </select>
            {formData.cliente_id && equiposFiltrados.length === 0 && (
              <p className="text-xs text-gray-400 italic">No hay equipos registrados para este cliente{formData.sucursal_nombre ? ` en "${formData.sucursal_nombre}"` : ''}.</p>
            )}
          </div>

          {/* ── Sección: Descripción ── */}
          <div className="space-y-3">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700 uppercase tracking-wide">
              <FileText className="w-4 h-4 text-emerald-500" />
              Descripción del Trabajo
            </h3>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Resumen <span className="text-red-500">*</span></label>
              <input
                type="text"
                name="descripcion"
                value={formData.descripcion}
                onChange={handleInputChange}
                disabled={!puedeEditar()}
                placeholder="Ej: Reemplazo de fuente de alimentación y limpieza general"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Detalle técnico</label>
              <textarea
                name="detalle"
                value={formData.detalle}
                onChange={handleInputChange}
                disabled={!puedeEditar()}
                rows={4}
                placeholder="Descripción detallada del trabajo, componentes, especificaciones, pasos a realizar..."
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 resize-none"
              />
            </div>
          </div>

          {/* ── Sección: Documentación visual ── */}
          <div className="space-y-3">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700 uppercase tracking-wide">
              <ImageIcon className="w-4 h-4 text-emerald-500" />
              Documentación Visual
            </h3>
            <ImagenesPresupuesto 
              presupuestoId={presupuesto?._id}
              imagenes={formData.imagenes || []}
              onImagenesChange={handleImagenesChange}
              puedeEditar={puedeEditar()}
            />
          </div>

          {/* ── Sección: Notas internas (solo admin) ── */}
          {user.rol === 'admin' && (
            <div className="space-y-3">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700 uppercase tracking-wide">
                <StickyNote className="w-4 h-4 text-yellow-500" />
                Notas Internas
              </h3>
              <textarea
                name="notas_internas"
                value={formData.notas_internas}
                onChange={handleInputChange}
                rows={2}
                placeholder="Notas visibles solo para administradores..."
                className="w-full px-3 py-2.5 border border-yellow-200 bg-yellow-50 rounded-lg text-sm focus:ring-2 focus:ring-yellow-400 focus:border-transparent resize-none"
              />
            </div>
          )}

          {/* Estado actual (al editar) */}
          {presupuesto && (
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Estado actual:</span>
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                formData.estado === 'borrador'             ? 'bg-gray-100 text-gray-700' :
                formData.estado === 'verificado'           ? 'bg-emerald-100 text-emerald-700' :
                formData.estado === 'pendiente_aprobacion' ? 'bg-yellow-100 text-yellow-700' :
                formData.estado === 'aprobado'             ? 'bg-green-100 text-green-700' :
                formData.estado === 'rechazado'            ? 'bg-red-100 text-red-700' :
                formData.estado === 'entregado'            ? 'bg-purple-100 text-purple-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {formData.estado === 'borrador'             && 'Borrador'}
                {formData.estado === 'verificado'           && 'Verificado'}
                {formData.estado === 'pendiente_aprobacion' && 'Pendiente Aprobación'}
                {formData.estado === 'aprobado'             && 'Aprobado'}
                {formData.estado === 'rechazado'            && 'Rechazado'}
                {formData.estado === 'entregado'            && 'Entregado'}
              </span>
            </div>
          )}
        </form>

        {/* Footer con botones */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-2xl">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {readOnly ? 'Cerrar' : 'Cancelar'}
          </button>
          {puedeEditar() && (
            <button
              type="submit"
              form="presupuesto-form"
              disabled={loading}
              onClick={handleSubmit}
              className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 shadow-sm"
            >
              <Save className="w-4 h-4" />
              {loading ? 'Guardando...' : presupuesto ? 'Guardar Cambios' : 'Crear Presupuesto'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PresupuestoForm;